import { type MouseEvent, type MouseEventHandler, useCallback, useState } from 'react';
import { toast } from 'sonner';

const { DEV } = import.meta.env;

const minDelay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

export const useLoadingCallback = <TElement extends Element = HTMLButtonElement>(
  onClick: MouseEventHandler<TElement> | undefined,
  onClickAsync: ((e: MouseEvent<TElement>) => Promise<unknown> | void) | undefined,
  onActionComplete?: () => void,
) => {
  const [isLoading, setIsLoading] = useState(false);

  const wrappedFunction = useCallback(
    async (e: MouseEvent<TElement>) => {
      if (onClick) return onClick(e);
      if (!onClickAsync) return;
      if (isLoading) return; // Prevent multiple simultaneous calls
      setIsLoading(true);
      try {
        await Promise.all([onClickAsync(e), minDelay(300)]);
        onActionComplete?.();
      } catch (error) {
        /**
          Handle error gracefully, in order to prevent the toast to show, handle the error in onClickAsync, for example
          ```
            const handlePublish = async () => {
              try {
              } catch (error) {
                toast.error('Failed to create. Please try again.');
              }
            };
          ```
        */
        console.error(error);
        if (DEV) {
          toast.error('' + error);
        } else {
          toast.error('An error occurred. Please try again.');
        }
        throw error;
      } finally {
        setIsLoading(false);
      }
    },
    [onClick, onClickAsync, isLoading, onActionComplete],
  );

  return [isLoading, wrappedFunction] as const;
};
